import { STAFF_FEATURES } from '../constants';
import type { SchoolSettings } from '../types';

/** One feature’s flags in school.settings staff access maps. */
export type StaffFeatureAccessEntry = { view?: boolean; edit?: boolean };

/** A single role’s or authority level’s row in staffFeatureAccess / staffFeatureAccessByAuthority. */
export type StaffFeatureAccessRow = Record<string, StaffFeatureAccessEntry>;

export type StaffPermissionsMode = 'ROLE' | 'AUTHORITY';

export function getStaffPermissionsMode(school: { settings?: SchoolSettings } | undefined): StaffPermissionsMode {
  return school?.settings?.staffFeaturePermissionsMode === 'AUTHORITY' ? 'AUTHORITY' : 'ROLE';
}

export function getStaffFeatureAccessMap(
  school: { settings?: SchoolSettings } | undefined,
): Record<string, StaffFeatureAccessRow> {
  const mode = getStaffPermissionsMode(school);
  return mode === 'AUTHORITY'
    ? (school?.settings?.staffFeatureAccessByAuthority as Record<string, StaffFeatureAccessRow> | undefined) ?? {}
    : (school?.settings?.staffFeatureAccess as Record<string, StaffFeatureAccessRow> | undefined) ?? {};
}

/**
 * Row for the current lookup key (role or authority level).
 * Missing key → undefined. Present but {} is an explicit empty row (deny all features).
 */
export function getStaffFeatureAccessRow(
  school: { settings?: SchoolSettings } | undefined,
  lookupKey: string | undefined,
): StaffFeatureAccessRow | undefined {
  if (!lookupKey) return undefined;
  const map = getStaffFeatureAccessMap(school);
  return map[lookupKey];
}

export function isEmptyAccessRow(row: StaffFeatureAccessRow | undefined): boolean {
  return !row || Object.keys(row).length === 0;
}

export function hasFeaturePermission(
  row: StaffFeatureAccessRow | undefined,
  featureId: string,
  isPrincipal: boolean,
): boolean {
  if (isPrincipal) return true;
  if (isEmptyAccessRow(row)) return false;
  const p = row![featureId];
  return !!(p?.view || p?.edit);
}

/** Any of the given feature ids allows view or edit (non-principals only; pass isPrincipal separately). */
export function hasAnyStaffFeature(
  row: StaffFeatureAccessRow | undefined,
  featureIds: readonly string[],
  isPrincipal: boolean,
): boolean {
  if (isPrincipal) return true;
  if (isEmptyAccessRow(row)) return false;
  return featureIds.some((id) => !!(row![id]?.view || row![id]?.edit));
}

/**
 * Feature ids for the whole Staff app section (nav + /staff/* routes).
 * Derived from STAFF_FEATURES so new staff-scoped features are included automatically.
 */
export const STAFF_AREA_FEATURE_IDS: readonly string[] = Array.from(
  new Set(STAFF_FEATURES.filter((f) => f.path.startsWith('/staff')).map((f) => f.id)),
).sort((a, b) => a.localeCompare(b));

export function hasStaffAreaAggregateAccess(row: StaffFeatureAccessRow | undefined, isPrincipal: boolean): boolean {
  return hasAnyStaffFeature(row, STAFF_AREA_FEATURE_IDS, isPrincipal);
}

/** Staff list tab: umbrella `staff` or roster `staffList`. */
export function canUseStaffListTab(row: StaffFeatureAccessRow | undefined, isPrincipal: boolean): boolean {
  return hasAnyStaffFeature(row, ['staff', 'staffList'], isPrincipal);
}

export function canUseStaffSubjectsTab(row: StaffFeatureAccessRow | undefined, isPrincipal: boolean): boolean {
  return hasAnyStaffFeature(row, ['staffSubjects'], isPrincipal);
}

export function canUseStaffRolesTab(row: StaffFeatureAccessRow | undefined, isPrincipal: boolean): boolean {
  return hasAnyStaffFeature(row, ['staffRoles'], isPrincipal);
}

export function canUseAuthorityLevelsTab(row: StaffFeatureAccessRow | undefined, isPrincipal: boolean): boolean {
  return canUseStaffRolesTab(row, isPrincipal);
}

/**
 * Drawer paths → feature id (must match keys persisted under staffFeatureAccess*).
 * Keep aligned with routes and with `STAFF_FEATURES` paths/ids.
 */
export const DRAWER_PATH_TO_FEATURE_ID: Record<string, string> = {
  '/': 'dashboard',
  '/dispersals': 'dispersals',
  '/students': 'students',
  '/students/relations': 'studentsRelations',
  '/students/dispersal': 'studentsDispersal',
  '/notifications': 'notifications',
  '/meetings': 'meetings',
  '/staff': 'staff',
  '/staff/subjects': 'staffSubjects',
  '/staff/roles': 'staffRoles',
  '/staff/authority-levels': 'staffRoles',
  '/classes': 'classes',
  '/notices': 'notices',
  '/school': 'school',
  '/gates': 'gates',
  '/settings': 'settings',
  '/staff-role-permissions': 'staffRolePermissions',
  '/payments': 'payments',
};

export function isDrawerPathFeatureGated(path: string): boolean {
  return Object.prototype.hasOwnProperty.call(DRAWER_PATH_TO_FEATURE_ID, path);
}

export function shouldShowDrawerItem(
  itemPath: string,
  row: StaffFeatureAccessRow | undefined,
  isPrincipal: boolean,
): boolean {
  if (isPrincipal) return true;
  if (!isDrawerPathFeatureGated(itemPath)) return true;
  if (isEmptyAccessRow(row)) return false;
  if (itemPath === '/staff') {
    return hasStaffAreaAggregateAccess(row, false);
  }
  const featureId = DRAWER_PATH_TO_FEATURE_ID[itemPath];
  if (!featureId) return true;
  return hasFeaturePermission(row, featureId, false);
}

export function filterStaffWebDrawerNav<T extends { path: string }>(
  items: T[],
  row: StaffFeatureAccessRow | undefined,
  isPrincipal: boolean,
  hasLookupKey: boolean,
): T[] {
  if (isPrincipal) return items;
  if (!hasLookupKey) {
    return items.filter((item) => !isDrawerPathFeatureGated(item.path));
  }
  if (isEmptyAccessRow(row)) {
    return items.filter((item) => !isDrawerPathFeatureGated(item.path));
  }
  return items.filter((item) => shouldShowDrawerItem(item.path, row, false));
}
